import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  NEW_CUSTOMER_BILLING_COMPACT_LINE,
  PROFILE_BEFORE_SUBSCRIBE_TITLE,
  PROFILE_TAX_ADDRESS_REQUIRED_NOTE,
} from "../../constants/newCustomerBilling";
import BillingWelcomeNotice from "../BillingWelcomeNotice";

const getTaxNoticeDismissKey = (userKey) => {
  const id = String(userKey || "").trim();
  return id
    ? `wowzer.taxAddressNotice.dismissed.${id}`
    : "wowzer.taxAddressNotice.dismissed";
};

const isTaxNoticeDismissed = (userKey) => {
  if (typeof window === "undefined" || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(getTaxNoticeDismissKey(userKey)) === "1";
  } catch (_) {
    return false;
  }
};

const dismissTaxNotice = (userKey) => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(getTaxNoticeDismissKey(userKey), "1");
  } catch (_) {
    /* no-op */
  }
};

function TaxAddressRequiredNotice({ userKey }) {
  const [dismissed, setDismissed] = useState(() =>
    isTaxNoticeDismissed(userKey)
  );

  useEffect(() => {
    setDismissed(isTaxNoticeDismissed(userKey));
  }, [userKey]);

  if (dismissed) return null;

  return (
    <div
      role="note"
      className="relative mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 pr-10 text-amber-900"
    >
      <p className="font-semibold text-base mb-1 m-0">
        {PROFILE_BEFORE_SUBSCRIBE_TITLE}
      </p>
      <p className="text-sm leading-relaxed m-0">
        {PROFILE_TAX_ADDRESS_REQUIRED_NOTE}
      </p>
      <button
        type="button"
        aria-label="Dismiss notice"
        onClick={() => {
          dismissTaxNotice(userKey);
          setDismissed(true);
        }}
        className="absolute top-2 right-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-amber-800/70 hover:text-amber-950 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-3.5 w-3.5"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
}

/**
 * Profile tab: billing notice + tax/address reminder (both dismissible).
 */
export default function ProfileBillingNotice({ companyData }) {
  const { user } = useAuth0();
  const userKey = user?.sub || user?.email || "";

  const hasTaxAndAddress =
    Boolean(String(companyData?.company_tax_number || "").trim()) &&
    Boolean(String(companyData?.company_address || "").trim());

  return (
    <>
      <BillingWelcomeNotice variant="compact" className="mb-6" dismissible />
      {!hasTaxAndAddress ? (
        <TaxAddressRequiredNotice userKey={userKey} />
      ) : null}
    </>
  );
}

export function ProfileBillingNoticePreview() {
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-blue-900">
      <span className="block text-lg font-bold uppercase tracking-wide mb-1">
        First 30 days free
      </span>
      <p className="text-sm">{NEW_CUSTOMER_BILLING_COMPACT_LINE}</p>
    </div>
  );
}
